import React, { useEffect, useMemo, useState } from 'react';
import ReactECharts from 'echarts-for-react';
import {
  Bar,
  BarChart,
  LabelList,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { Heading } from '../layout';
import { useUIColors } from '../../hooks/useTheme';
import API_BASE_URL from '../../config/api';
import { useBusinessUnitApi } from '../../contexts/BusinessUnitContext';
import {
  PERIOD_OPTIONS,
  MONTH_OPTIONS,
  collectDates,
  getAvailableYearsFromDates,
  resolvePeriodSelection,
  isDateInRange,
  parseDateSafe,
  periodSelectStyle,
  periodLabelStyle,
} from './periodSummaryUtils';
import { normalizeCommercialSegment } from './RegionSegmentStackedChart';

const ORG_TOP_METRIC_OPTIONS = [
  { value: 'amount', label: 'Monto de ventas' },
  { value: 'count', label: 'Cantidad de ventas' },
];

const COMMERCIAL_SEGMENTS = ['B2G', 'B2B', 'B2C', 'B2B2C', 'Sin Asignar'];

/** Respuestas JSON: siempre lista para evitar fallos si el backend envía envoltorio u objeto. */
const asArray = (raw) => {
  if (Array.isArray(raw)) return raw;
  if (raw && typeof raw === 'object') {
    if (Array.isArray(raw.data)) return raw.data;
    if (Array.isArray(raw.items)) return raw.items;
    if (Array.isArray(raw.results)) return raw.results;
  }
  return [];
};

const PALETTE = {
  quoted: '#334155',
  sales: '#0F766E',
  sky: '#0369A1',
  amber: '#B45309',
  green: '#15803D',
  coral: '#C2410C',
  rose: '#BE123C',
  slate: '#64748B',
  trackLight: '#E2E8F0',
  trackDark: '#27272A',
};

const STATUS_COLORS = {
  Borrador: '#94A3B8',
  Emitida: '#2563EB',
  Adjudicada: '#15803D',
  Vendida: '#0F766E',
  Desierta: '#64748B',
  Cancelada: '#BE123C',
  Perdida: '#C2410C',
};

const SERIES_COLORS = ['#0F766E', '#0369A1', '#B45309', '#334155', '#15803D', '#BE123C', '#64748B', '#0E7490'];

const foldStatusKey = (s) =>
  String(s || '')
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/\p{M}/gu, '');

const normalizeStatus = (status = '') => {
  const key = foldStatusKey(status);
  const map = {
    aprobada: 'Adjudicada',
    adjudicada: 'Adjudicada',
    enviada: 'Emitida',
    emitida: 'Emitida',
    rechazada: 'Rechazada',
    perdida: 'Perdida',
    borrador: 'Borrador',
    desierta: 'Desierta',
    cancelada: 'Cancelada',
  };
  if (map[key]) return map[key];
  const normalized = String(status || '').trim();
  if (normalized === 'Aprobada') return 'Adjudicada';
  if (normalized === 'Enviada') return 'Emitida';
  return normalized || 'Borrador';
};

const quotationPrimaryDate = (q) => q?.quotation_date || q?.created_at || '';
const salePrimaryDate = (s) => s?.invoice_date || s?.quotation_date || s?.deal_close_date || s?.created_at || '';
const licitationPrimaryDate = (l) => l?.closing_date || l?.adjudication_date || l?.created_at || l?.updated_at;
const logisticsPrimaryDate = (x) => x?.scheduled_dispatch_date || x?.delivered_date || x?.created_at || x?.updated_at;

const isOpenLicitationStatus = (status) => {
  const k = foldStatusKey(status);
  return k === 'abierta' || k === 'en analisis' || k === 'oferta enviada';
};

const isDeliveredLogistics = (status) => foldStatusKey(status) === 'entregado';

const isClpLikeCurrency = (currency) => {
  const c = String(currency || 'CLP').trim().toUpperCase();
  return !c || c === 'CLP' || c === 'PESOS';
};

/** Total neto → total con IVA (19%). Misma regla que Resumen de Cotizaciones. */
const grossAmountConIva = (netTotal) => {
  const t = Number(netTotal || 0);
  if (!Number.isFinite(t) || t === 0) return 0;
  return Math.round(t * 1.19 * 100) / 100;
};

/** Monto comparable con la tabla de Cotizaciones: solo CLP, c/IVA. */
const commercialAmount = (row) => {
  if (!isClpLikeCurrency(row?.currency)) return 0;
  return grossAmountConIva(row?.total_amount);
};

const parseAmount = (value) => Number(value || 0);
const safeDivide = (num, den) => (den > 0 ? num / den : 0);
const formatPercent = (value) => `${Number(value || 0).toFixed(1)}%`;
const formatCompact = (value) => {
  const n = Number(value || 0);
  const abs = Math.abs(n);
  if (abs >= 1_000_000) {
    const millions = n / 1_000_000;
    // Evitar "B": montos grandes se expresan siempre en millones.
    if (abs >= 1_000_000_000) {
      return `${millions.toLocaleString('es-CL', { maximumFractionDigits: 0 })}M`;
    }
    return `${millions.toFixed(1)}M`;
  }
  if (abs >= 1_000) return `${(n / 1_000).toFixed(0)}k`;
  return n.toLocaleString('es-CL');
};
const formatCurrencyCompact = (value) => `$ ${formatCompact(value)}`;
const formatCount = (value) => Number(value || 0).toLocaleString('es-CL');

const filterByRange = (records, getDate, range) =>
  (Array.isArray(records) ? records : []).filter((row) => isDateInRange(getDate(row), range));

const panelBorder = (ui) =>
  ui.isDarkMode ? '1px solid rgba(255,255,255,0.08)' : '1px solid rgba(15, 23, 42, 0.08)';
const panelBg = (ui) => (ui.isDarkMode ? 'rgba(255,255,255,0.02)' : 'rgba(255,255,255,0.72)');
const trackBg = (ui) => (ui.isDarkMode ? PALETTE.trackDark : PALETTE.trackLight);

/** Dos anchos fijos: ancho (charts) y estrecho (métricas laterales). */
const SUMMARY_LAYOUT_COLUMNS = 'minmax(0, 1.4fr) minmax(0, 0.9fr)';

const ORIGIN_METRIC_OPTIONS = [
  { value: 'amount', label: 'Monto c/IVA' },
  { value: 'count', label: 'Cantidad' },
];

/** Clave estable de organización para historial comercial. */
const organizationKey = (row) => {
  const id = Number(row?.organization_id);
  if (Number.isFinite(id) && id > 0) return `id:${id}`;
  const name = String(row?.organization_name || '').trim().toLowerCase();
  return name ? `name:${name}` : '';
};

/**
 * Marca cada registro si la misma org ya tuvo actividad anterior
 * (cotización previa / compra previa). Orden: fecha ASC, id ASC.
 */
const buildPriorActivityFlags = (records, getDate, getId = (row) => row?.id) => {
  const sorted = (Array.isArray(records) ? records : [])
    .map((row, index) => ({ row, index, date: parseDateSafe(getDate(row)) }))
    .filter((item) => item.date)
    .sort((a, b) => {
      const byDate = a.date.getTime() - b.date.getTime();
      if (byDate !== 0) return byDate;
      const byId = Number(getId(a.row) || 0) - Number(getId(b.row) || 0);
      if (byId !== 0) return byId;
      return a.index - b.index;
    });

  const seenOrgs = new Set();
  const flags = new Map();
  sorted.forEach(({ row }) => {
    const recordId = Number(getId(row));
    const key = organizationKey(row);
    const hadPrior = Boolean(key && seenOrgs.has(key));
    if (Number.isFinite(recordId) && recordId > 0) flags.set(recordId, hadPrior);
    if (key) seenOrgs.add(key);
  });
  return flags;
};

const summarizeOriginSplit = (records, flags, getAmount = commercialAmount) => {
  const summary = {
    repeatCount: 0,
    newCount: 0,
    repeatAmount: 0,
    newAmount: 0,
  };
  (Array.isArray(records) ? records : []).forEach((row) => {
    const isRepeat = flags.get(Number(row?.id)) === true;
    const amount = getAmount(row);
    if (isRepeat) {
      summary.repeatCount += 1;
      summary.repeatAmount += amount;
    } else {
      summary.newCount += 1;
      summary.newAmount += amount;
    }
  });
  summary.totalCount = summary.repeatCount + summary.newCount;
  summary.totalAmount = summary.repeatAmount + summary.newAmount;
  summary.repeatCountShare = safeDivide(summary.repeatCount, summary.totalCount) * 100;
  summary.repeatAmountShare = safeDivide(summary.repeatAmount, summary.totalAmount) * 100;
  summary.newCountShare = safeDivide(summary.newCount, summary.totalCount) * 100;
  summary.newAmountShare = safeDivide(summary.newAmount, summary.totalAmount) * 100;
  return summary;
};

const buildOriginBarsByPeriod = (records, flags, getDate, columns, getAmount = commercialAmount) =>
  (columns || []).map((column) => {
    const inBucket = (Array.isArray(records) ? records : []).filter((row) => {
      const date = parseDateSafe(getDate(row));
      return Boolean(date && date >= column.start && date <= column.end);
    });
    const split = summarizeOriginSplit(inBucket, flags, getAmount);
    return {
      label: column.label,
      recurrentAmount: split.repeatAmount,
      firstAmount: split.newAmount,
      recurrentCount: split.repeatCount,
      firstCount: split.newCount,
      totalAmount: split.totalAmount,
      totalCount: split.totalCount,
    };
  });

const SectionPanel = ({ title, children, ui, style, right }) => (
  <section
    style={{
      border: panelBorder(ui),
      borderRadius: 8,
      padding: '8px 10px',
      background: panelBg(ui),
      minWidth: 0,
      maxWidth: '100%',
      height: 'fit-content',
      overflow: 'hidden',
      boxSizing: 'border-box',
      ...style,
    }}
  >
    {(title || right) && (
      <div
        style={{
          display: 'flex',
          alignItems: 'baseline',
          justifyContent: 'space-between',
          gap: 8,

          marginBottom: 6,
          minWidth: 0,
        }}
      >
        {title ? (
          <p
            style={{
              margin: 0,
              fontSize: 10,
              fontWeight: 700,
              letterSpacing: '0.06em',
              textTransform: 'uppercase',
              color: ui.muted,
              minWidth: 0,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {title}
          </p>
        ) : (
          <span />
        )}
        {right || null}
      </div>
    )}
    <div style={{ minWidth: 0, maxWidth: '100%', overflow: 'hidden' }}>{children}</div>
  </section>
);

const MetricCell = ({ label, value, hint, accent, ui }) => (
  <div style={{ minWidth: 0, padding: '2px 4px' }}>
    <p
      style={{
        margin: 0,
        fontSize: 9,
        fontWeight: 650,
        letterSpacing: '0.04em',
        textTransform: 'uppercase',
        color: ui.muted,
        lineHeight: 1.15,
      }}
    >
      {label}
    </p>
    <p
      style={{
        margin: '2px 0 0',
        fontSize: 15,
        fontWeight: 700,
        letterSpacing: '-0.02em',
        color: accent || ui.title,
        fontVariantNumeric: 'tabular-nums',
        lineHeight: 1.1,
      }}
    >
      {value}
    </p>
    {hint ? (
      <p style={{ margin: '2px 0 0', fontSize: 10, color: ui.muted, lineHeight: 1.25 }}>{hint}</p>
    ) : null}
  </div>
);

const BreakdownRows = ({ rows, ui, valueMode = 'currency', compact = false }) => {
  const total = rows.reduce((sum, row) => sum + Number(row.value || 0), 0);
  if (!total) {
    return <p style={{ margin: 0, fontSize: 12, color: ui.muted }}>Sin datos en el período</p>;
  }
  return (
    <div style={{ display: 'grid', gap: compact ? 4 : 5 }}>
      {rows.map((row, index) => {
        const value = Number(row.value || 0);
        const pct = safeDivide(value, total) * 100;
        const display =
          valueMode === 'count'
            ? formatCount(value)
            : valueMode === 'percent'
              ? formatPercent(value)
              : formatCurrencyCompact(value);
        return (
          <div key={`${row.label}-${index}`} style={{ display: 'grid', gap: 2, minWidth: 0 }}>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'minmax(0, 1fr) auto',
                gap: 6,
                fontSize: compact ? 11 : 12,
                fontVariantNumeric: 'tabular-nums',
                minWidth: 0,
              }}
            >
              <span style={{ color: ui.title, display: 'inline-flex', alignItems: 'center', gap: 5, minWidth: 0 }}>
                <span
                  style={{
                    width: 7,
                    height: 7,
                    borderRadius: 2,
                    background: row.color || SERIES_COLORS[index % SERIES_COLORS.length],
                    flexShrink: 0,
                  }}
                />
                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{row.label}</span>
              </span>
              <span style={{ color: ui.muted, whiteSpace: 'nowrap' }}>
                <strong style={{ color: ui.title, fontWeight: 700 }}>{display}</strong>
                {' · '}
                {formatPercent(pct)}
                {row.extra ? ` · ${row.extra}` : ''}
              </span>
            </div>
            <div
              aria-hidden
              style={{
                height: 4,
                minHeight: 4,
                maxHeight: 4,
                width: '100%',
                borderRadius: 2,
                backgroundImage: `linear-gradient(to right, ${row.color || SERIES_COLORS[index % SERIES_COLORS.length]} 0%, ${row.color || SERIES_COLORS[index % SERIES_COLORS.length]} ${Math.max(pct, value > 0 ? 1.5 : 0)}%, ${trackBg(ui)} ${Math.max(pct, value > 0 ? 1.5 : 0)}%, ${trackBg(ui)} 100%)`,
                backgroundRepeat: 'no-repeat',
                backgroundSize: '100% 100%',
                flexShrink: 0,
              }}
            />
          </div>
        );
      })}
    </div>
  );
};

const RankingList = ({
  rows,
  ui,
  color = PALETTE.sales,
  showShareOf,
  valueMode = 'currency',
  secondaryMode = null,
}) => {
  if (!rows.length) {
    return <p style={{ margin: 0, fontSize: 11, color: ui.muted }}>Sin datos en el período</p>;
  }
  const max = Math.max(...rows.map((row) => Number(row.value || 0)), 1);
  const shareBase = Number(showShareOf || 0);
  const formatPrimary = (row) => {
    const value = Number(row.value || 0);
    if (valueMode === 'count') return `${formatCount(value)} u.`;
    return formatCurrencyCompact(value);
  };
  const formatSecondary = (row) => {
    if (!secondaryMode) return null;
    if (secondaryMode === 'count') return `${formatCount(row.count || 0)} u.`;
    if (secondaryMode === 'amount') return formatCurrencyCompact(row.amount || 0);
    return null;
  };
  return (
    <div style={{ display: 'grid', gap: 2, minWidth: 0, maxWidth: '100%' }}>
      {rows.map((row, index) => {
        const value = Number(row.value || 0);
        const pct = safeDivide(value, max) * 100;
        const share = shareBase > 0 ? safeDivide(value, shareBase) * 100 : null;
        const secondary = formatSecondary(row);
        const metrics = [formatPrimary(row), secondary, share != null ? formatPercent(share) : null]
          .filter(Boolean)
          .join(' · ');
        return (
          <div key={`${row.label}-${index}`} style={{ display: 'grid', gap: 1, minWidth: 0 }}>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'minmax(0, 1fr) auto',
                columnGap: 8,
                alignItems: 'center',
                minWidth: 0,
                fontSize: 11,
                fontVariantNumeric: 'tabular-nums',
                lineHeight: 1.2,
              }}
            >
              <span
                style={{
                  color: ui.title,
                  minWidth: 0,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
                title={row.label}
              >
                <span style={{ color: ui.muted }}>{index + 1}. </span>
                {row.label}
              </span>
              <strong
                style={{
                  color: ui.title,
                  fontWeight: 650,
                  whiteSpace: 'nowrap',
                  fontSize: 10,
                }}
                title={metrics}
              >
                {metrics}
              </strong>
            </div>
            <div
              aria-hidden
              style={{
                height: 4,
                minHeight: 4,
                maxHeight: 4,
                width: '100%',
                borderRadius: 2,
                backgroundImage: `linear-gradient(to right, ${color} 0%, ${color} ${Math.max(pct, 2)}%, ${trackBg(ui)} ${Math.max(pct, 2)}%, ${trackBg(ui)} 100%)`,
                backgroundRepeat: 'no-repeat',
                backgroundSize: '100% 100%',
                flexShrink: 0,
              }}
            />
          </div>
        );
      })}
    </div>
  );
};

const buildTopOrganizationsFromSales = (salesSet, { metric = 'amount', segment = 'all', limit = 10 } = {}) => {
  const filtered = (Array.isArray(salesSet) ? salesSet : []).filter((sale) => {
    if (!isClpLikeCurrency(sale.currency)) return false;
    if (segment === 'all') return true;
    return normalizeCommercialSegment(sale.organization_segment) === segment;
  });
  const acc = {};
  filtered.forEach((sale) => {
    const key = String(sale.organization_name || 'Sin organización').trim() || 'Sin organización';
    if (!acc[key]) {
      acc[key] = { label: key, amount: 0, count: 0, segment: normalizeCommercialSegment(sale.organization_segment) };
    }
    acc[key].amount += commercialAmount(sale);
    acc[key].count += 1;
  });
  return Object.values(acc)
    .map((row) => ({
      ...row,
      value: metric === 'count' ? row.count : row.amount,
    }))
    .sort((a, b) => b.value - a.value || b.amount - a.amount)
    .slice(0, limit);
};

const RankingBarChart = ({ rows, color = PALETTE.quoted, ui }) => {
  if (!rows.length) {
    return <p style={{ margin: 0, fontSize: 12, color: ui.muted }}>Sin datos en el período</p>;
  }
  const data = rows.map((row) => ({
    name: row.label.length > 16 ? `${row.label.slice(0, 14)}…` : row.label,
    value: Number(row.value || 0),
    label: formatCurrencyCompact(row.value),
  }));
  return (
    <div style={{ width: '100%', height: Math.max(120, data.length * 24) }}>
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={data} layout="vertical" margin={{ top: 0, right: 52, left: 0, bottom: 0 }}>
          <XAxis type="number" hide />
          <YAxis
            type="category"
            dataKey="name"
            width={84}
            tick={{ fill: ui.muted, fontSize: 10 }}
            axisLine={false}
            tickLine={false}
          />
          <Bar dataKey="value" fill={color} radius={[0, 2, 2, 0]} barSize={9}>
            <LabelList
              dataKey="label"
              position="right"
              style={{ fill: ui.title, fontSize: 10, fontWeight: 600 }}
            />
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

const PeriodTrendEChart = ({ columns, quotedBars, salesBars, ui }) => {
  const categories = (columns || []).map((column) => column.label);
  const quoted = (columns || []).map((_, index) => Number(quotedBars[index]?.value || 0));
  const sold = (columns || []).map((_, index) => Number(salesBars[index]?.value || 0));
  if (!categories.length) {
    return <p style={{ margin: 0, fontSize: 12, color: ui.muted }}>Sin serie temporal</p>;
  }

  const dense = categories.length > 8;
  const option = {
    color: [PALETTE.sales, PALETTE.quoted],
    grid: { left: 46, right: 10, top: 36, bottom: dense ? 42 : 28 },
    legend: {
      top: 0,
      left: 0,
      itemWidth: 10,
      itemHeight: 10,
      textStyle: { color: ui.muted, fontSize: 11 },
      data: ['Cotizado', 'Ventas'],
    },
    tooltip: { show: false },
    xAxis: {
      type: 'category',
      data: categories,
      axisLabel: {
        color: ui.muted,
        fontSize: 10,
        hideOverlap: true,
        rotate: dense ? 35 : 0,
      },
      axisLine: { lineStyle: { color: ui.isDarkMode ? '#3f3f46' : '#CBD5E1' } },
      axisTick: { show: false },
    },
    yAxis: {
      type: 'value',
      axisLabel: {
        color: ui.muted,
        fontSize: 10,
        formatter: (value) => formatCompact(value),
      },
      splitLine: {
        lineStyle: { color: ui.isDarkMode ? 'rgba(255,255,255,0.06)' : 'rgba(15,23,42,0.06)', type: 'dashed' },
      },
      axisLine: { show: false },
      axisTick: { show: false },
    },
    series: [
      {
        name: 'Cotizado',
        type: 'bar',
        data: quoted,
        barMaxWidth: 16,
        itemStyle: { borderRadius: [3, 3, 0, 0], color: PALETTE.sales },
        label: {
          show: !dense,
          position: 'top',
          color: ui.muted,
          fontSize: 9,
          formatter: ({ value }) => (value ? formatCompact(value) : ''),
        },
      },
      {
        name: 'Ventas',
        type: 'line',
        data: sold,
        smooth: true,
        symbolSize: 7,
        lineStyle: { width: 2.5, color: PALETTE.quoted },
        itemStyle: { color: PALETTE.quoted },
        label: {
          show: true,
          position: 'top',
          color: PALETTE.quoted,
          fontSize: 10,
          fontWeight: 650,
          formatter: ({ value }) => (value ? formatCompact(value) : ''),
        },
      },
    ],
  };

  const tableRows = categories.map((label, index) => ({
    label,
    quoted: quoted[index],
    sold: sold[index],
    gap: quoted[index] - sold[index],
    conversion: safeDivide(sold[index], quoted[index]) * 100,
  }));

  return (
    <div style={{ display: 'grid', gap: 6 }}>
      <ReactECharts option={option} style={{ height: 168, width: '100%' }} opts={{ renderer: 'canvas' }} notMerge />
      <div style={{ overflowX: 'auto' }}>
        <table
          style={{
            width: '100%',
            borderCollapse: 'collapse',
            fontSize: 10,
            fontVariantNumeric: 'tabular-nums',
          }}
        >
          <thead>
            <tr style={{ color: ui.muted, textAlign: 'left' }}>
              <th style={{ fontWeight: 650, padding: '2px 4px 2px 0' }}>Período</th>
              <th style={{ fontWeight: 650, padding: '2px 4px', textAlign: 'right' }}>Cotizado</th>
              <th style={{ fontWeight: 650, padding: '2px 4px', textAlign: 'right' }}>Ventas</th>
              <th style={{ fontWeight: 650, padding: '2px 4px', textAlign: 'right' }}>Brecha</th>
              <th style={{ fontWeight: 650, padding: '2px 0 2px 4px', textAlign: 'right' }}>Conv.</th>
            </tr>
          </thead>
          <tbody>
            {tableRows.map((row) => (
              <tr key={row.label} style={{ borderTop: panelBorder(ui) }}>
                <td style={{ padding: '3px 4px 3px 0', color: ui.title }}>{row.label}</td>
                <td style={{ padding: '3px 4px', textAlign: 'right', color: ui.title }}>{formatCurrencyCompact(row.quoted)}</td>
                <td style={{ padding: '3px 4px', textAlign: 'right', color: PALETTE.quoted }}>{formatCurrencyCompact(row.sold)}</td>
                <td style={{ padding: '3px 4px', textAlign: 'right', color: ui.muted }}>{formatCurrencyCompact(row.gap)}</td>
                <td style={{ padding: '3px 0 3px 4px', textAlign: 'right', color: ui.muted }}>{formatPercent(row.conversion)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const OriginSplitChart = ({
  rows,
  metric,
  ui,
  emptyText = 'Sin datos en el período',
  helpText = '',
  recurrentSeriesName = 'Recurrentes',
  firstSeriesName = 'Primera vez',
  firstColumnLabel = '1ª vez',
}) => {
  const hasData = rows.some((row) => Number(row.totalCount || 0) > 0 || Number(row.totalAmount || 0) > 0);
  if (!rows.length || !hasData) {
    return <p style={{ margin: 0, fontSize: 12, color: ui.muted }}>{emptyText}</p>;
  }
  const categories = rows.map((row) => row.label);
  const recurrent = rows.map((row) =>
    metric === 'count' ? Number(row.recurrentCount || 0) : Number(row.recurrentAmount || 0)
  );
  const firstSeries = rows.map((row) =>
    metric === 'count' ? Number(row.firstCount || 0) : Number(row.firstAmount || 0)
  );
  const totals = rows.map((_, index) => Number(recurrent[index] || 0) + Number(firstSeries[index] || 0));
  const dense = categories.length > 8;
  const formatValue = (value) =>
    metric === 'count' ? formatCount(value) : formatCurrencyCompact(value);
  const formatAxis = (value) => (metric === 'count' ? formatCount(value) : formatCompact(value));

  const option = {
    color: [PALETTE.sales, PALETTE.sky],
    grid: { left: 46, right: 10, top: 36, bottom: dense ? 42 : 28 },
    legend: {
      top: 0,
      left: 0,
      itemWidth: 10,
      itemHeight: 10,
      textStyle: { color: ui.muted, fontSize: 11 },
      data: [recurrentSeriesName, firstSeriesName],
    },
    tooltip: { show: false },
    xAxis: {
      type: 'category',
      data: categories,
      axisLabel: {
        color: ui.muted,
        fontSize: 10,
        hideOverlap: true,
        rotate: dense ? 35 : 0,
      },
      axisLine: { lineStyle: { color: ui.isDarkMode ? '#3f3f46' : '#CBD5E1' } },
      axisTick: { show: false },
    },
    yAxis: {
      type: 'value',
      axisLabel: {
        color: ui.muted,
        fontSize: 10,
        formatter: (value) => formatAxis(value),
      },
      splitLine: {
        lineStyle: { color: ui.isDarkMode ? 'rgba(255,255,255,0.06)' : 'rgba(15,23,42,0.06)', type: 'dashed' },
      },
      axisLine: { show: false },
      axisTick: { show: false },
    },
    series: [
      {
        name: recurrentSeriesName,
        type: 'bar',
        stack: 'origin',
        data: recurrent,
        barMaxWidth: 18,
        itemStyle: { color: PALETTE.sales },
      },
      {
        name: firstSeriesName,
        type: 'bar',
        stack: 'origin',
        data: firstSeries,
        barMaxWidth: 18,
        itemStyle: { borderRadius: [3, 3, 0, 0], color: PALETTE.sky },
        label: {
          show: true,
          position: 'top',
          distance: 4,
          color: ui.title,
          fontSize: 9,
          fontWeight: 650,
          formatter: ({ dataIndex }) => {
            const total = totals[dataIndex];
            if (!total) return '';
            return formatPercent(safeDivide(recurrent[dataIndex], total) * 100);
          },
        },
      },
    ],
  };

  return (
    <div style={{ display: 'grid', gap: 6 }}>
      {helpText ? <p style={{ margin: 0, fontSize: 10, color: ui.muted }}>{helpText}</p> : null}
      <ReactECharts option={option} style={{ height: 168, width: '100%' }} opts={{ renderer: 'canvas' }} notMerge />
      <div style={{ overflowX: 'auto' }}>
        <table
          style={{
            width: '100%',
            borderCollapse: 'collapse',
            fontSize: 10,
            fontVariantNumeric: 'tabular-nums',
          }}
        >
          <thead>
            <tr style={{ color: ui.muted, textAlign: 'left' }}>
              <th style={{ fontWeight: 650, padding: '2px 4px 2px 0' }}>Período</th>
              <th style={{ fontWeight: 650, padding: '2px 4px', textAlign: 'right' }}>Recurrentes</th>
              <th style={{ fontWeight: 650, padding: '2px 4px', textAlign: 'right' }}>{firstColumnLabel}</th>
              <th style={{ fontWeight: 650, padding: '2px 0 2px 4px', textAlign: 'right' }}>% Rec.</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => {
              const recurrentValue = metric === 'count' ? row.recurrentCount : row.recurrentAmount;
              const firstValue = metric === 'count' ? row.firstCount : row.firstAmount;
              const total = metric === 'count' ? row.totalCount : row.totalAmount;
              return (
                <tr key={row.label} style={{ borderTop: panelBorder(ui) }}>
                  <td style={{ padding: '3px 4px 3px 0', color: ui.title }}>{row.label}</td>
                  <td style={{ padding: '3px 4px', textAlign: 'right', color: PALETTE.sales }}>
                    {formatValue(recurrentValue)}
                    {metric === 'amount' ? (
                      <span style={{ color: ui.muted }}> · {formatCount(row.recurrentCount)}</span>
                    ) : null}
                  </td>
                  <td style={{ padding: '3px 4px', textAlign: 'right', color: PALETTE.sky }}>
                    {formatValue(firstValue)}
                    {metric === 'amount' ? (
                      <span style={{ color: ui.muted }}> · {formatCount(row.firstCount)}</span>
                    ) : null}
                  </td>
                  <td style={{ padding: '3px 0 3px 4px', textAlign: 'right', color: ui.muted }}>
                    {formatPercent(safeDivide(recurrentValue, total) * 100)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const MiniStatGrid = ({ items, ui, columns = 2 }) => (
  <div
    style={{
      display: 'grid',
      gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))`,
      gap: 5,
    }}
  >
    {items.map((item) => (
      <div
        key={item.label}
        style={{
          border: panelBorder(ui),
          borderRadius: 6,
          padding: '5px 7px',
          background: ui.isDarkMode ? 'rgba(0,0,0,0.15)' : 'rgba(248,250,252,0.9)',
        }}
      >
        <p style={{ margin: 0, fontSize: 9, fontWeight: 650, letterSpacing: '0.04em', textTransform: 'uppercase', color: ui.muted }}>
          {item.label}
        </p>
        <p
          style={{
            margin: '2px 0 0',
            fontSize: 13,
            fontWeight: 700,
            color: item.accent || ui.title,
            fontVariantNumeric: 'tabular-nums',
          }}
        >
          {item.value}
        </p>
        {item.hint ? <p style={{ margin: '1px 0 0', fontSize: 10, color: ui.muted, lineHeight: 1.2 }}>{item.hint}</p> : null}
      </div>
    ))}
  </div>
);

const DataTable = ({ columns, rows, ui, empty = 'Sin datos' }) => {
  if (!rows.length) {
    return <p style={{ margin: 0, fontSize: 11, color: ui.muted }}>{empty}</p>;
  }
  return (
    <div style={{ overflowX: 'auto' }}>
      <table
        style={{
          width: '100%',
          borderCollapse: 'collapse',
          fontSize: 10,
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        <thead>
          <tr style={{ color: ui.muted, textAlign: 'left' }}>
            {columns.map((col) => (
              <th
                key={col.key}
                style={{
                  fontWeight: 650,
                  padding: '2px 4px',
                  textAlign: col.align || 'left',
                  whiteSpace: 'nowrap',
                }}
              >
                {col.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.id || row.label} style={{ borderTop: panelBorder(ui) }}>
              {columns.map((col) => (
                <td
                  key={col.key}
                  style={{
                    padding: '3px 4px',
                    textAlign: col.align || 'left',
                    color: col.color ? col.color(row) : ui.title,
                    fontWeight: col.bold ? 650 : 400,
                    maxWidth: col.maxWidth || 'none',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {col.render ? col.render(row) : row[col.key]}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const buildStatusBars = (quoteSet) => {
  const statuses = ['Borrador', 'Emitida', 'Adjudicada', 'Vendida', 'Desierta', 'Cancelada', 'Perdida'];
  return statuses.map((status) => {
    const rows = quoteSet.filter((q) => q.status === status);
    const amount = rows.reduce((acc, q) => acc + commercialAmount(q), 0);
    return {
      label: status,
      value: rows.length,
      amount,
      color: STATUS_COLORS[status],
      extra: formatCurrencyCompact(amount),
    };
  });
};

const buildAmountGroup = (rows, getKey, getAmount = commercialAmount, limit = 8) => {
  const acc = {};
  rows.forEach((row) => {
    if (!isClpLikeCurrency(row.currency) && getAmount === commercialAmount) return;
    const key = String(getKey(row) || 'Sin dato').trim() || 'Sin dato';
    acc[key] = (acc[key] || 0) + getAmount(row);
  });
  return Object.entries(acc)
    .map(([label, value], index) => ({
      label,
      value,
      color: SERIES_COLORS[index % SERIES_COLORS.length],
    }))
    .sort((a, b) => b.value - a.value)
    .slice(0, limit);
};

const buildCountGroup = (rows, getKey, limit = 8) => {
  const acc = {};
  rows.forEach((row) => {
    const key = String(getKey(row) || 'Sin dato').trim() || 'Sin dato';
    acc[key] = (acc[key] || 0) + 1;
  });
  return Object.entries(acc)
    .map(([label, value], index) => ({
      label,
      value,
      color: SERIES_COLORS[index % SERIES_COLORS.length],
    }))
    .sort((a, b) => b.value - a.value)
    .slice(0, limit);
};

const buildTopWon = (quoteSet, getKey, limit = 6) => {
  const acc = {};
  quoteSet
    .filter((q) => q.status === 'Adjudicada' || q.status === 'Vendida')
    .forEach((q) => {
      const key = String(getKey(q) || 'Sin dato').trim() || 'Sin dato';
      acc[key] = (acc[key] || 0) + commercialAmount(q);
    });
  return Object.entries(acc)
    .map(([label, value]) => ({ label, value }))
    .sort((a, b) => b.value - a.value)
    .slice(0, limit);
};

const buildExecutivePerformance = (quoteSet) => {
  const map = {};
  quoteSet.forEach((q) => {
    const key = q.user_full_name || 'Sin ejecutivo';
    if (!map[key]) {
      map[key] = {
        label: key,
        quotes: 0,
        draft: 0,
        emitted: 0,
        won: 0,
        lost: 0,
        quoted: 0,
        openAmount: 0,
        wonAmount: 0,
        lostAmount: 0,
      };
    }
    const row = map[key];
    row.quotes += 1;
    row.quoted += commercialAmount(q);
    if (q.status === 'Borrador') {
      row.draft += 1;
      row.openAmount += commercialAmount(q);
    }
    if (q.status === 'Emitida') {
      row.emitted += 1;
      row.openAmount += commercialAmount(q);
    }
    if (q.status === 'Adjudicada' || q.status === 'Vendida') {
      row.won += 1;
      row.wonAmount += commercialAmount(q);
    }
    if (q.status === 'Perdida' || q.status === 'Rechazada') {
      row.lost += 1;
      row.lostAmount += commercialAmount(q);
    }
  });
  return Object.values(map)
    .map((row) => ({
      ...row,
      id: row.label,
      open: row.draft + row.emitted,
      winRate: safeDivide(row.won, row.won + row.lost) * 100,
      avgTicket: safeDivide(row.quoted, row.quotes),
    }))
    .sort((a, b) => b.wonAmount - a.wonAmount)
    .slice(0, 8);
};

const TICKET_STATUS_STACK = ['Borrador', 'Emitida', 'Adjudicada', 'Vendida', 'Desierta', 'Cancelada', 'Perdida'];

const TICKET_INTERVALS = [
  { label: '<0.5M', min: 0, max: 500_000 },
  { label: '0.5–1M', min: 500_000, max: 1_000_000 },
  { label: '1–2M', min: 1_000_000, max: 2_000_000 },
  { label: '2–3M', min: 2_000_000, max: 3_000_000 },
  { label: '3–5M', min: 3_000_000, max: 5_000_000 },
  { label: '5–10M', min: 5_000_000, max: 10_000_000 },
  { label: '10–20M', min: 10_000_000, max: 20_000_000 },
  { label: '20–50M', min: 20_000_000, max: 50_000_000 },
  { label: '≥50M', min: 50_000_000, max: Infinity },
];

const buildTicketDistribution = (quoteSet) =>
  TICKET_INTERVALS.map((interval) => {
    const inBucket = (Array.isArray(quoteSet) ? quoteSet : []).filter((q) => {
      const amount = commercialAmount(q);
      return amount >= interval.min && amount < interval.max;
    });
    const row = {
      label: interval.label,
      total: 0,
      countTotal: inBucket.length,
      counts: { total: inBucket.length },
    };
    TICKET_STATUS_STACK.forEach((status) => {
      const statusRows = inBucket.filter((q) => q.status === status);
      const amount = statusRows.reduce((acc, q) => acc + commercialAmount(q), 0);
      row[status] = amount;
      row.counts[status] = statusRows.length;
      row.total += amount;
    });
    return row;
  }).filter((row) => row.countTotal > 0);

const formatAxisMillions = (value) => {
  const n = Number(value || 0);
  if (!Number.isFinite(n) || n === 0) return '$0';
  if (Math.abs(n) >= 1_000_000) {
    const millions = n / 1_000_000;
    if (Math.abs(n) >= 1_000_000_000) {
      return `$${millions.toLocaleString('es-CL', { maximumFractionDigits: 0 })}M`;
    }
    return `$${millions >= 10 ? millions.toFixed(0) : millions.toFixed(1)}M`;
  }
  if (Math.abs(n) >= 1_000) return `$${(n / 1_000).toFixed(0)}k`;
  return `$${n.toLocaleString('es-CL')}`;
};

const formatFullCurrency = (value) =>
  `$ ${Number(value || 0).toLocaleString('es-CL', { maximumFractionDigits: 0 })}`;

const TicketAmountTooltip = ({ active, payload, label, ui }) => {
  if (!active || !payload?.length) return null;
  const row = payload[0]?.payload || {};
  const items = payload.filter((item) => Number(item.value || 0) > 0);
  return (
    <div
      style={{
        background: ui.isDarkMode ? '#18181b' : '#fff',
        border: panelBorder(ui),
        borderRadius: 8,
        padding: '8px 10px',
        boxShadow: '0 8px 20px rgba(15, 23, 42, 0.12)',
        minWidth: 180,
      }}
    >
      <p style={{ margin: 0, fontSize: 11, fontWeight: 700, color: ui.title }}>Tramo {label}</p>
      <p style={{ margin: '2px 0 6px', fontSize: 10, color: ui.muted }}>Montos c/IVA (CLP)</p>
      <div style={{ display: 'grid', gap: 3 }}>
        {items.map((item) => (
          <div
            key={item.dataKey}
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              gap: 12,
              fontSize: 11,
              fontVariantNumeric: 'tabular-nums',
            }}
          >
            <span style={{ color: ui.muted, display: 'inline-flex', alignItems: 'center', gap: 5 }}>
              <span
                style={{
                  width: 7,
                  height: 7,
                  borderRadius: 2,
                  background: item.color || STATUS_COLORS[item.dataKey] || '#94A3B8',
                  flexShrink: 0,
                }}
              />
              {item.name}
              {row.counts?.[item.dataKey] != null ? ` (${row.counts[item.dataKey]})` : ''}
            </span>
            <strong style={{ color: ui.title, fontWeight: 650 }}>{formatFullCurrency(item.value)}</strong>
          </div>
        ))}
      </div>
      <div
        style={{
          marginTop: 6,
          paddingTop: 6,
          borderTop: panelBorder(ui),
          display: 'flex',
          justifyContent: 'space-between',
          gap: 12,
          fontSize: 11,
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        <span style={{ color: ui.muted }}>
          Total{row.countTotal != null ? ` (${row.countTotal} cot.)` : ''}
        </span>
        <strong style={{ color: ui.title }}>{formatFullCurrency(row.total)}</strong>
      </div>
    </div>
  );
};

const TicketDistributionChart = ({ rows, ui }) => {
  if (!rows.length) {
    return <p style={{ margin: 0, fontSize: 11, color: ui.muted }}>Sin cotizaciones en el período</p>;
  }
  const activeStatuses = TICKET_STATUS_STACK.filter((status) =>
    rows.some((row) => Number(row[status] || 0) > 0 || Number(row.counts?.[status] || 0) > 0)
  );
  return (
    <div style={{ width: '100%', minWidth: 0 }}>
      <p style={{ margin: '0 0 4px', fontSize: 10, color: ui.muted }}>
        Gráfico: montos c/IVA · Tabla: cantidad de cotizaciones
      </p>
      <div style={{ width: '100%', height: 280 }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={rows} margin={{ top: 22, right: 8, left: 8, bottom: 0 }} barCategoryGap="14%">
            <XAxis
              dataKey="label"
              tick={{ fill: ui.muted, fontSize: 9 }}
              axisLine={false}
              tickLine={false}
              interval={0}
              angle={-28}
              textAnchor="end"
              height={42}
            />
            <YAxis
              tick={{ fill: ui.muted, fontSize: 10 }}
              axisLine={false}
              tickLine={false}
              width={52}
              tickFormatter={formatAxisMillions}
              domain={[0, (dataMax) => Math.ceil(Number(dataMax || 0) * 1.12) || 1]}
              label={{
                value: 'Monto c/IVA',
                angle: -90,
                position: 'insideLeft',
                offset: 4,
                style: { fill: ui.muted, fontSize: 10, textAnchor: 'middle' },
              }}
            />
            <Tooltip
              cursor={{ fill: ui.isDarkMode ? 'rgba(255,255,255,0.04)' : 'rgba(15,23,42,0.04)' }}
              content={<TicketAmountTooltip ui={ui} />}
            />
            <Legend
              verticalAlign="top"
              align="left"
              iconSize={8}
              wrapperStyle={{ fontSize: 10, color: ui.muted, paddingBottom: 4 }}
            />
            {activeStatuses.map((status, index) => (
              <Bar
                key={status}
                dataKey={status}
                name={status}
                stackId="ticket"
                fill={STATUS_COLORS[status] || SERIES_COLORS[index % SERIES_COLORS.length]}
                maxBarSize={36}
              >
                {index === activeStatuses.length - 1 ? (
                  <LabelList
                    dataKey="total"
                    content={({ x, y, width, value }) => {
                      if (!value || x == null || y == null || width == null) return null;
                      return (
                        <text
                          x={Number(x) + Number(width) / 2}
                          y={Number(y) - 6}
                          fill={ui.title}
                          fontSize={9}
                          fontWeight={650}
                          textAnchor="middle"
                          dominantBaseline="auto"
                        >
                          {formatAxisMillions(value)}
                        </text>
                      );
                    }}
                  />
                ) : null}
              </Bar>
            ))}
          </BarChart>
        </ResponsiveContainer>
      </div>
      <div style={{ overflowX: 'auto', marginTop: 4 }}>
        <table
          style={{
            width: '100%',
            borderCollapse: 'collapse',
            fontSize: 10,
            fontVariantNumeric: 'tabular-nums',
          }}
        >
          <thead>
            <tr style={{ color: ui.muted }}>
              <th style={{ textAlign: 'left', fontWeight: 650, padding: '2px 4px 2px 0' }}>Tramo</th>
              {activeStatuses.map((status) => (
                <th key={status} style={{ textAlign: 'right', fontWeight: 650, padding: '2px 3px' }}>
                  {status.slice(0, 4)}.
                </th>
              ))}
              <th style={{ textAlign: 'right', fontWeight: 650, padding: '2px 0 2px 3px' }}>Tot.</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.label} style={{ borderTop: panelBorder(ui) }}>
                <td style={{ padding: '2px 4px 2px 0', color: ui.title }}>{row.label}</td>
                {activeStatuses.map((status) => (
                  <td key={status} style={{ textAlign: 'right', padding: '2px 3px', color: ui.muted }}>
                    {row.counts?.[status] || 0}
                  </td>
                ))}
                <td style={{ textAlign: 'right', padding: '2px 0 2px 3px', color: ui.title, fontWeight: 650 }}>
                  {row.countTotal}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const buildPeriodAmountBars = (records, getDate, columns, getAmount = commercialAmount) =>
  columns.map((column) => {
    const value = records
      .filter((row) => {
        const date = parseDateSafe(getDate(row));
        return date && date >= column.start && date <= column.end;
      })
      .reduce((acc, row) => acc + getAmount(row), 0);
    return { label: column.label, value };
  });

const calculateCompositeMetrics = (quoteSet, salesSet, licitationsSet, logisticsSet) => {
  const totalQuotes = quoteSet.length;
  const emittedQuotes = quoteSet.filter((q) => q.status !== 'Borrador').length;
  const draftQuotes = quoteSet.filter((q) => q.status === 'Borrador').length;
  const emitidaQuotes = quoteSet.filter((q) => q.status === 'Emitida').length;
  const openQuotes = quoteSet.filter((q) => q.status === 'Emitida' || q.status === 'Borrador').length;
  const adjudicatedQuotes = quoteSet.filter((q) => q.status === 'Adjudicada').length;
  const soldQuotes = quoteSet.filter((q) => q.status === 'Vendida').length;
  const lostQuotes = quoteSet.filter((q) => q.status === 'Perdida' || q.status === 'Rechazada').length;
  const cancelledQuotes = quoteSet.filter((q) => q.status === 'Cancelada' || q.status === 'Desierta').length;
  const wonQuotes = adjudicatedQuotes + soldQuotes;
  const decidedQuotes = wonQuotes + lostQuotes;
  const conversion = safeDivide(wonQuotes, emittedQuotes) * 100;
  const winRate = safeDivide(soldQuotes, soldQuotes + lostQuotes) * 100;
  const hitRate = safeDivide(wonQuotes, decidedQuotes) * 100;

  // Montos alineados con "Resumen cotizaciones": solo CLP y c/IVA.
  const quotedAmount = quoteSet.reduce((acc, q) => acc + commercialAmount(q), 0);
  const draftAmount = quoteSet
    .filter((q) => q.status === 'Borrador')
    .reduce((acc, q) => acc + commercialAmount(q), 0);
  const emitidaAmount = quoteSet
    .filter((q) => q.status === 'Emitida')
    .reduce((acc, q) => acc + commercialAmount(q), 0);
  const pipelineAmount = draftAmount + emitidaAmount;
  const adjudicatedAmount = quoteSet
    .filter((q) => q.status === 'Adjudicada' || q.status === 'Vendida')
    .reduce((acc, q) => acc + commercialAmount(q), 0);
  const soldAmount = quoteSet
    .filter((q) => q.status === 'Vendida')
    .reduce((acc, q) => acc + commercialAmount(q), 0);
  const lostAmount = quoteSet
    .filter((q) => q.status === 'Perdida' || q.status === 'Rechazada')
    .reduce((acc, q) => acc + commercialAmount(q), 0);
  const cancelledAmount = quoteSet
    .filter((q) => q.status === 'Cancelada' || q.status === 'Desierta')
    .reduce((acc, q) => acc + commercialAmount(q), 0);

  // Ventas KPI = monto vendido c/IVA (misma definición que la tabla de Cotizaciones).
  const salesAmount = soldAmount;
  const salesCount = soldQuotes;

  // Margen sigue en neto desde módulo Ventas (estimated_cost es neto).
  const salesNetAmount = salesSet
    .filter((s) => isClpLikeCurrency(s.currency))
    .reduce((acc, s) => acc + parseAmount(s.total_amount), 0);
  const estimatedCost = salesSet
    .filter((s) => isClpLikeCurrency(s.currency))
    .reduce((acc, s) => acc + parseAmount(s.estimated_cost), 0);
  const estimatedMargin = salesNetAmount - estimatedCost;
  const marginPct = safeDivide(estimatedMargin, salesNetAmount) * 100;
  const avgQuoteTicket = safeDivide(quotedAmount, totalQuotes);
  const avgSaleTicket = safeDivide(salesAmount, salesCount);
  const avgWonTicket = safeDivide(adjudicatedAmount, wonQuotes);
  const avgLostTicket = safeDivide(lostAmount, lostQuotes);
  const quoteToSale = safeDivide(salesAmount, quotedAmount) * 100;
  const pipelineShare = safeDivide(pipelineAmount, quotedAmount) * 100;
  const lossRatio = safeDivide(lostAmount, adjudicatedAmount + lostAmount) * 100;
  const coverage = safeDivide(adjudicatedAmount, pipelineAmount) * 100;

  const uniqueOrgs = new Set(quoteSet.map((q) => q.organization_name || 'Sin organización')).size;
  const uniqueExecs = new Set(quoteSet.map((q) => q.user_full_name || 'Sin ejecutivo')).size;
  const activeOrgsWon = new Set(
    quoteSet
      .filter((q) => q.status === 'Adjudicada' || q.status === 'Vendida')
      .map((q) => q.organization_name || 'Sin organización')
  ).size;

  const totalLicitations = licitationsSet.length;
  const openLicitations = licitationsSet.filter((l) => isOpenLicitationStatus(l.status)).length;
  const wonLicitations = licitationsSet.filter((l) => foldStatusKey(l.status) === 'adjudicada').length;
  const lostLicitations = licitationsSet.filter((l) => foldStatusKey(l.status) === 'perdida').length;
  const licitationAmount = licitationsSet.reduce((acc, l) => acc + parseAmount(l.estimated_amount), 0);
  const openLicitationAmount = licitationsSet
    .filter((l) => isOpenLicitationStatus(l.status))
    .reduce((acc, l) => acc + parseAmount(l.estimated_amount), 0);
  const wonLicitationAmount = licitationsSet
    .filter((l) => foldStatusKey(l.status) === 'adjudicada')
    .reduce((acc, l) => acc + parseAmount(l.estimated_amount), 0);
  const licitationWinRate = safeDivide(wonLicitations, wonLicitations + lostLicitations) * 100;

  const totalLogistics = logisticsSet.length;
  const deliveredLogistics = logisticsSet.filter((l) => isDeliveredLogistics(l.dispatch_status)).length;
  const incidentLogistics = logisticsSet.filter((l) => foldStatusKey(l.dispatch_status) === 'incidencia').length;
  const pendingLogistics = logisticsSet.filter((l) => {
    const k = foldStatusKey(l.dispatch_status);
    return k === 'programado' || k === 'en transito' || k === 'pendiente' || k === '';
  }).length;
  const logisticsDeliveryRate = safeDivide(deliveredLogistics, totalLogistics) * 100;
  const logisticsIncidentRate = safeDivide(incidentLogistics, totalLogistics) * 100;

  return {
    totalQuotes,
    emittedQuotes,
    draftQuotes,
    emitidaQuotes,
    openQuotes,
    adjudicatedQuotes,
    soldQuotes,
    lostQuotes,
    cancelledQuotes,
    wonQuotes,
    decidedQuotes,
    conversion,
    winRate,
    hitRate,
    quotedAmount,
    draftAmount,
    emitidaAmount,
    pipelineAmount,
    adjudicatedAmount,
    soldAmount,
    lostAmount,
    cancelledAmount,
    salesAmount,
    salesCount,
    salesNetAmount,
    estimatedMargin,
    marginPct,
    avgQuoteTicket,
    avgSaleTicket,
    avgWonTicket,
    avgLostTicket,
    quoteToSale,
    pipelineShare,
    lossRatio,
    coverage,
    uniqueOrgs,
    uniqueExecs,
    activeOrgsWon,
    totalLicitations,
    openLicitations,
    wonLicitations,
    lostLicitations,
    licitationAmount,
    openLicitationAmount,
    wonLicitationAmount,
    licitationWinRate,
    totalLogistics,
    deliveredLogistics,
    incidentLogistics,
    pendingLogistics,
    logisticsDeliveryRate,
    logisticsIncidentRate,
  };
};

const PaintCommercialSummaryWorkspace = ({ isDarkMode = false }) => {
  const ui = useUIColors();
  const { authFetch } = useBusinessUnitApi();
  const [quotations, setQuotations] = useState([]);
  const [sales, setSales] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [licitations, setLicitations] = useState([]);
  const [logistics, setLogistics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [periodFilter, setPeriodFilter] = useState('last_12');
  const [secondaryYear, setSecondaryYear] = useState('all');
  const [secondaryMonth, setSecondaryMonth] = useState(String(new Date().getMonth()));
  const [orgTopMetric, setOrgTopMetric] = useState('amount');
  const [orgSegmentFilter, setOrgSegmentFilter] = useState('all');
  const [ticketSegmentFilter, setTicketSegmentFilter] = useState('all');
  const [salesOriginMetric, setSalesOriginMetric] = useState('amount');
  const [salesOriginSegmentFilter, setSalesOriginSegmentFilter] = useState('all');
  const [quoteOriginMetric, setQuoteOriginMetric] = useState('amount');
  const [quoteOriginSegmentFilter, setQuoteOriginSegmentFilter] = useState('all');

  const loadData = async () => {
    setLoading(true);
    setError('');
    try {
      const [quotesRes, salesRes, orgsRes, licitationsRes, logisticsRes] = await Promise.all([
        authFetch(`${API_BASE_URL}/api/quotations/`),
        authFetch(`${API_BASE_URL}/api/quotations/sales`),
        authFetch(`${API_BASE_URL}/api/organizations/`),
        authFetch(`${API_BASE_URL}/api/quotations/licitations`),
        authFetch(`${API_BASE_URL}/api/quotations/logistics`),
      ]);
      if (!quotesRes.ok) throw new Error(`Quotations HTTP ${quotesRes.status}`);
      if (!salesRes.ok) throw new Error(`Sales HTTP ${salesRes.status}`);
      if (!orgsRes.ok) throw new Error(`Organizations HTTP ${orgsRes.status}`);
      if (!licitationsRes.ok) throw new Error(`Licitations HTTP ${licitationsRes.status}`);
      if (!logisticsRes.ok) throw new Error(`Logistics HTTP ${logisticsRes.status}`);

      const quotesRaw = await quotesRes.json();
      const salesRaw = await salesRes.json();
      const orgsRaw = await orgsRes.json();
      const licitationsRaw = await licitationsRes.json();
      const logisticsRaw = await logisticsRes.json();

      setQuotations(
        asArray(quotesRaw).map((quote) => ({
          ...quote,
          status: normalizeStatus(quote.status),
        }))
      );
      setSales(asArray(salesRaw));
      setOrganizations(asArray(orgsRaw));
      setLicitations(asArray(licitationsRaw));
      setLogistics(asArray(logisticsRaw));
    } catch (fetchError) {
      console.error('Error loading summary dashboard:', fetchError);
      setQuotations([]);
      setSales([]);
      setOrganizations([]);
      setLicitations([]);
      setLogistics([]);
      setError('No se pudo cargar el dashboard de resumen.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const allDateValues = useMemo(
    () => [
      ...collectDates(quotations, quotationPrimaryDate),
      ...collectDates(sales, salePrimaryDate),
      ...collectDates(licitations, licitationPrimaryDate),
      ...collectDates(logistics, logisticsPrimaryDate),
    ],
    [quotations, sales, licitations, logistics]
  );

  const availableYears = useMemo(() => getAvailableYearsFromDates(allDateValues), [allDateValues]);

  const selectedPeriod = useMemo(
    () =>
      resolvePeriodSelection({
        period: periodFilter,
        dateValues: allDateValues,
        secondaryYear,
        secondaryMonth,
        titles: {
          monthly: 'Resumen comercial mensual',
          annual: 'Resumen comercial anual',
        },
      }),
    [periodFilter, allDateValues, secondaryYear, secondaryMonth]
  );

  useEffect(() => {
    if (!availableYears.length) return;
    if (periodFilter === 'annual' || periodFilter === 'annual_ytd' || periodFilter === 'quarterly' || periodFilter === 'semestral') {
      if (secondaryYear !== 'all' && !availableYears.includes(Number(secondaryYear))) {
        setSecondaryYear('all');
      }
      return;
    }
    if (periodFilter === 'monthly') {
      if (!availableYears.includes(Number(secondaryYear))) {
        setSecondaryYear(String(availableYears[availableYears.length - 1]));
      }
    }
  }, [availableYears, periodFilter, secondaryYear]);

  const handlePeriodFilterChange = (nextPeriod) => {
    setPeriodFilter(nextPeriod);
    const now = new Date();
    const latestYear = availableYears[availableYears.length - 1] || now.getFullYear();
    if (nextPeriod === 'annual' || nextPeriod === 'annual_ytd' || nextPeriod === 'quarterly' || nextPeriod === 'semestral') {
      setSecondaryYear('all');
      return;
    }
    if (nextPeriod === 'monthly') {
      setSecondaryYear(String(availableYears.includes(now.getFullYear()) ? now.getFullYear() : latestYear));
      setSecondaryMonth(String(now.getMonth()));
    }
  };

  const periodQuotes = useMemo(
    () => filterByRange(quotations, quotationPrimaryDate, selectedPeriod.range),
    [quotations, selectedPeriod]
  );
  const periodSales = useMemo(
    () => filterByRange(sales, salePrimaryDate, selectedPeriod.range),
    [sales, selectedPeriod]
  );
  const periodLicitations = useMemo(
    () => filterByRange(licitations, licitationPrimaryDate, selectedPeriod.range),
    [licitations, selectedPeriod]
  );
  const periodLogistics = useMemo(
    () => filterByRange(logistics, logisticsPrimaryDate, selectedPeriod.range),
    [logistics, selectedPeriod]
  );

  const quotePriorFlags = useMemo(
    () => buildPriorActivityFlags(quotations, quotationPrimaryDate),
    [quotations]
  );
  const salePriorFlags = useMemo(
    () => buildPriorActivityFlags(sales, salePrimaryDate),
    [sales]
  );
  const salesForOrigin = useMemo(() => {
    const base = periodSales.filter((sale) => isClpLikeCurrency(sale.currency));
    if (salesOriginSegmentFilter === 'all') return base;
    return base.filter(
      (sale) => normalizeCommercialSegment(sale.organization_segment) === salesOriginSegmentFilter
    );
  }, [periodSales, salesOriginSegmentFilter]);
  const salesOrigin = useMemo(
    () => summarizeOriginSplit(salesForOrigin, salePriorFlags, commercialAmount),
    [salesForOrigin, salePriorFlags]
  );
  const salesOriginBars = useMemo(
    () =>
      buildOriginBarsByPeriod(
        salesForOrigin,
        salePriorFlags,
        salePrimaryDate,
        selectedPeriod.columns,
        commercialAmount
      ),
    [salesForOrigin, salePriorFlags, selectedPeriod.columns]
  );

  const metrics = useMemo(
    () => calculateCompositeMetrics(periodQuotes, periodSales, periodLicitations, periodLogistics),
    [periodQuotes, periodSales, periodLicitations, periodLogistics]
  );

  const salesYtd = useMemo(() => {
    const now = new Date();
    const yearStart = new Date(now.getFullYear(), 0, 1);
    const rows = filterByRange(quotations, quotationPrimaryDate, { start: yearStart, end: now }).filter(
      (q) => q.status === 'Vendida' && isClpLikeCurrency(q.currency)
    );
    return {
      year: now.getFullYear(),
      total: rows.reduce((sum, q) => sum + commercialAmount(q), 0),
      orders: rows.length,
    };
  }, [quotations]);

  const statusBars = useMemo(() => buildStatusBars(periodQuotes), [periodQuotes]);
  const funnelRows = useMemo(
    () =>
      statusBars
        .filter((row) => Number(row.value || 0) > 0)
        .map((row) => ({ ...row, value: row.value, extra: formatCurrencyCompact(row.amount) })),
    [statusBars]
  );
  const topOrganizations = useMemo(
    () =>
      buildTopOrganizationsFromSales(periodSales, {
        metric: orgTopMetric,
        segment: orgSegmentFilter,
        limit: 10,
      }),
    [periodSales, orgTopMetric, orgSegmentFilter]
  );
  const topExecutives = useMemo(() => buildTopWon(periodQuotes, (q) => q.user_full_name || 'Sin ejecutivo'), [periodQuotes]);

  const orgSegmentOptions = useMemo(() => {
    const discovered = new Set();
    periodSales.forEach((sale) => {
      discovered.add(normalizeCommercialSegment(sale.organization_segment));
    });
    const ordered = COMMERCIAL_SEGMENTS.filter((segment) => discovered.has(segment));
    COMMERCIAL_SEGMENTS.forEach((segment) => {
      if (!ordered.includes(segment) && discovered.has(segment)) ordered.push(segment);
    });
    Array.from(discovered)
      .filter((segment) => !ordered.includes(segment))
      .sort()
      .forEach((segment) => ordered.push(segment));
    return ordered;
  }, [periodSales]);

  const filteredSalesForOrgTop = useMemo(() => {
    const base = periodSales.filter((sale) => isClpLikeCurrency(sale.currency));
    if (orgSegmentFilter === 'all') return base;
    return base.filter(
      (sale) => normalizeCommercialSegment(sale.organization_segment) === orgSegmentFilter
    );
  }, [periodSales, orgSegmentFilter]);

  const orgTopShareBase = useMemo(() => {
    if (orgTopMetric === 'count') return filteredSalesForOrgTop.length;
    return filteredSalesForOrgTop.reduce((sum, sale) => sum + commercialAmount(sale), 0);
  }, [filteredSalesForOrgTop, orgTopMetric]);
  useEffect(() => {
    if (orgSegmentFilter === 'all') return;
    if (!orgSegmentOptions.includes(orgSegmentFilter)) {
      setOrgSegmentFilter('all');
    }
  }, [orgSegmentOptions, orgSegmentFilter]);

  useEffect(() => {
    if (salesOriginSegmentFilter === 'all') return;
    if (!orgSegmentOptions.includes(salesOriginSegmentFilter)) {
      setSalesOriginSegmentFilter('all');
    }
  }, [orgSegmentOptions, salesOriginSegmentFilter]);

  const channelMix = useMemo(
    () => buildAmountGroup(periodQuotes, (q) => q.channel || 'Sin canal'),
    [periodQuotes]
  );
  const logisticsStatusMix = useMemo(
    () => buildCountGroup(periodLogistics, (l) => l.dispatch_status || 'Sin estado'),
    [periodLogistics]
  );
  const licitationStatusMix = useMemo(
    () => buildCountGroup(periodLicitations, (l) => l.status || 'Sin estado'),
    [periodLicitations]
  );
  const quoteSegmentByOrgId = useMemo(() => {
    const map = new Map();
    // Prefer organization catalog (covers quotes without sales).
    organizations.forEach((organization) => {
      const orgId = Number(organization.id);
      if (!Number.isFinite(orgId) || orgId <= 0) return;
      map.set(orgId, normalizeCommercialSegment(organization.segment));
    });
    // Fill gaps from sales payload (historical / inactive orgs).
    sales.forEach((sale) => {
      const orgId = Number(sale.organization_id);
      if (!Number.isFinite(orgId) || orgId <= 0 || map.has(orgId)) return;
      map.set(orgId, normalizeCommercialSegment(sale.organization_segment));
    });
    return map;
  }, [organizations, sales]);

  const resolveQuoteSegment = (quote) => {
    const fromQuote = normalizeCommercialSegment(quote?.organization_segment);
    if (fromQuote !== 'Sin Asignar') return fromQuote;
    const orgId = Number(quote?.organization_id);
    if (Number.isFinite(orgId) && orgId > 0 && quoteSegmentByOrgId.has(orgId)) {
      return quoteSegmentByOrgId.get(orgId);
    }
    return fromQuote;
  };

  const quotesForOrigin = useMemo(() => {
    const base = periodQuotes.filter((quote) => isClpLikeCurrency(quote.currency));
    if (quoteOriginSegmentFilter === 'all') return base;
    return base.filter((quote) => resolveQuoteSegment(quote) === quoteOriginSegmentFilter);
  }, [periodQuotes, quoteOriginSegmentFilter, quoteSegmentByOrgId]);
  const quoteOrigin = useMemo(
    () => summarizeOriginSplit(quotesForOrigin, quotePriorFlags, commercialAmount),
    [quotesForOrigin, quotePriorFlags]
  );
  const quoteOriginBars = useMemo(
    () =>
      buildOriginBarsByPeriod(
        quotesForOrigin,
        quotePriorFlags,
        quotationPrimaryDate,
        selectedPeriod.columns,
        commercialAmount
      ),
    [quotesForOrigin, quotePriorFlags, selectedPeriod.columns]
  );

  const ticketSegmentOptions = useMemo(() => {
    const discovered = new Set();
    periodQuotes.forEach((quote) => {
      discovered.add(resolveQuoteSegment(quote));
    });
    const ordered = COMMERCIAL_SEGMENTS.filter((segment) => discovered.has(segment));
    Array.from(discovered)
      .filter((segment) => !ordered.includes(segment))
      .sort()
      .forEach((segment) => ordered.push(segment));
    return ordered;
  }, [periodQuotes, quoteSegmentByOrgId]);

  const filteredQuotesForTickets = useMemo(() => {
    if (ticketSegmentFilter === 'all') return periodQuotes;
    return periodQuotes.filter((quote) => resolveQuoteSegment(quote) === ticketSegmentFilter);
  }, [periodQuotes, ticketSegmentFilter, quoteSegmentByOrgId]);

  useEffect(() => {
    if (ticketSegmentFilter === 'all') return;
    if (!ticketSegmentOptions.includes(ticketSegmentFilter)) {
      setTicketSegmentFilter('all');
    }
  }, [ticketSegmentOptions, ticketSegmentFilter]);

  useEffect(() => {
    if (quoteOriginSegmentFilter === 'all') return;
    if (!ticketSegmentOptions.includes(quoteOriginSegmentFilter)) {
      setQuoteOriginSegmentFilter('all');
    }
  }, [ticketSegmentOptions, quoteOriginSegmentFilter]);

  const ticketDistribution = useMemo(
    () => buildTicketDistribution(filteredQuotesForTickets),
    [filteredQuotesForTickets]
  );
  const executivePerformance = useMemo(() => buildExecutivePerformance(periodQuotes), [periodQuotes]);

  const concentration = useMemo(() => {
    const ranked = buildTopOrganizationsFromSales(periodSales, {
      metric: 'amount',
      segment: orgSegmentFilter,
      limit: 10,
    });
    const scopedSales = periodSales.filter((sale) => {
      if (!isClpLikeCurrency(sale.currency)) return false;
      if (orgSegmentFilter === 'all') return true;
      return normalizeCommercialSegment(sale.organization_segment) === orgSegmentFilter;
    });
    const baseAmount = scopedSales.reduce((sum, sale) => sum + commercialAmount(sale), 0);
    const top3 = ranked.slice(0, 3).reduce((sum, row) => sum + row.amount, 0);
    const top1 = ranked[0]?.amount || 0;
    const orgsWon = new Set(scopedSales.map((sale) => sale.organization_name || 'Sin organización')).size;
    return {
      top1Share: safeDivide(top1, baseAmount) * 100,
      top3Share: safeDivide(top3, baseAmount) * 100,
      orgsWon,
      baseAmount,
    };
  }, [periodSales, orgSegmentFilter]);

  const quotedAmountBars = useMemo(
    () => buildPeriodAmountBars(periodQuotes, quotationPrimaryDate, selectedPeriod.columns, commercialAmount),
    [periodQuotes, selectedPeriod.columns]
  );
  const salesAmountBars = useMemo(
    () =>
      buildPeriodAmountBars(
        periodQuotes.filter((q) => q.status === 'Vendida'),
        quotationPrimaryDate,
        selectedPeriod.columns,
        commercialAmount
      ),
    [periodQuotes, selectedPeriod.columns]
  );

  const periodHelper = PERIOD_OPTIONS.find((option) => option.value === periodFilter)?.label || selectedPeriod.title;

  const resultMetrics = [
    {
      label: 'Ventas c/IVA',
      value: formatCurrencyCompact(metrics.salesAmount),
      hint: `${formatCount(metrics.salesCount)} vendidas · ticket ${formatCurrencyCompact(metrics.avgSaleTicket)}`,
      accent: PALETTE.sales,
    },
    {
      label: 'Margen est. (neto)',
      value: formatCurrencyCompact(metrics.estimatedMargin),
      hint: `${formatPercent(metrics.marginPct)} sobre ventas netas`,
      accent: metrics.estimatedMargin >= 0 ? PALETTE.green : PALETTE.rose,
    },
    {
      label: 'Adjudicado c/IVA',
      value: formatCurrencyCompact(metrics.adjudicatedAmount),
      hint: `${formatCount(metrics.wonQuotes)} ganadas · ticket ${formatCurrencyCompact(metrics.avgWonTicket)}`,
      accent: PALETTE.green,
    },
    {
      label: 'Perdido c/IVA',
      value: formatCurrencyCompact(metrics.lostAmount),
      hint: `${formatCount(metrics.lostQuotes)} · ticket ${formatCurrencyCompact(metrics.avgLostTicket)}`,
      accent: PALETTE.coral,
    },
    {
      label: `YTD ${salesYtd.year} c/IVA`,
      value: formatCurrencyCompact(salesYtd.total),
      hint: `${formatCount(salesYtd.orders)} vendidas del año`,
      accent: PALETTE.sky,
    },
    {
      label: 'Cotiz. → venta',
      value: formatPercent(metrics.quoteToSale),
      hint: 'Vendido / cotizado (c/IVA CLP)',
      accent: PALETTE.quoted,
    },
  ];

  const funnelMetrics = [
    {
      label: 'Cotizado c/IVA',
      value: formatCurrencyCompact(metrics.quotedAmount),
      hint: `${formatCount(metrics.totalQuotes)} cotiz. · ticket ${formatCurrencyCompact(metrics.avgQuoteTicket)}`,
    },
    {
      label: 'Pipeline',
      value: formatCurrencyCompact(metrics.pipelineAmount),
      hint: `${formatCount(metrics.openQuotes)} abiertas · ${formatPercent(metrics.pipelineShare)}`,
      accent: PALETTE.sky,
    },
    {
      label: 'Tasa cierre',
      value: formatPercent(metrics.winRate),
      hint: `${metrics.soldQuotes} vend. / ${metrics.soldQuotes + metrics.lostQuotes} resueltas`,
      accent: PALETTE.sales,
    },
    {
      label: 'Hit rate',
      value: formatPercent(metrics.hitRate),
      hint: `${metrics.wonQuotes} ganadas / ${metrics.decidedQuotes} decididas`,
    },
    {
      label: 'Conv. embudo',
      value: formatPercent(metrics.conversion),
      hint: `${metrics.wonQuotes} / ${metrics.emittedQuotes} emitidas`,
    },
    {
      label: 'Ratio pérdida',
      value: formatPercent(metrics.lossRatio),
      hint: 'Perdido / (ganado + perdido)',
      accent: PALETTE.coral,
    },
  ];

  return (
    <div>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'minmax(0, 1fr) auto',
          alignItems: 'center',
          columnGap: 16,
          rowGap: 8,
        }}
      >
        <div style={{ minWidth: 0 }}>
          <Heading level={2} isDarkMode={isDarkMode}>
            Resumen Comercial
          </Heading>
          <p style={{ margin: '4px 0 0', fontSize: 12, color: ui.muted }}>
            {selectedPeriod.title} · {periodHelper}
          </p>
        </div>
        <div
          style={{
            display: 'flex',
            alignItems: 'flex-end',
            justifyContent: 'flex-end',
            gap: 8,
            flexWrap: 'wrap',
            justifySelf: 'end',
          }}
        >
          <label style={{ display: 'flex', flexDirection: 'column', gap: 4, flex: '0 0 auto' }}>
            <span style={periodLabelStyle(ui)}>Período</span>
            <select
              value={periodFilter}
              onChange={(event) => handlePeriodFilterChange(event.target.value)}
              style={{ ...periodSelectStyle(ui), minWidth: 160 }}
            >
              {PERIOD_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>
          {periodFilter === 'annual' ||
          periodFilter === 'annual_ytd' ||
          periodFilter === 'quarterly' ||
          periodFilter === 'semestral' ? (
            <label style={{ display: 'flex', flexDirection: 'column', gap: 4, flex: '0 0 auto' }}>
              <span style={periodLabelStyle(ui)}>Año</span>
              <select
                value={secondaryYear}
                onChange={(event) => setSecondaryYear(event.target.value)}
                style={{ ...periodSelectStyle(ui), minWidth: 100 }}
              >
                <option value="all">Todas</option>
                {availableYears.map((year) => (
                  <option key={year} value={String(year)}>
                    {year}
                  </option>
                ))}
              </select>
            </label>
          ) : null}
          {periodFilter === 'monthly' ? (
            <>
              <label style={{ display: 'flex', flexDirection: 'column', gap: 4, flex: '0 0 auto' }}>
                <span style={periodLabelStyle(ui)}>Año</span>
                <select
                  value={String(
                    secondaryYear === 'all'
                      ? availableYears[availableYears.length - 1] || new Date().getFullYear()
                      : secondaryYear
                  )}
                  onChange={(event) => setSecondaryYear(event.target.value)}
                  style={{ ...periodSelectStyle(ui), minWidth: 90 }}
                >
                  {availableYears.map((year) => (
                    <option key={year} value={String(year)}>
                      {year}
                    </option>
                  ))}
                </select>
              </label>
              <label style={{ display: 'flex', flexDirection: 'column', gap: 4, flex: '0 0 auto' }}>
                <span style={periodLabelStyle(ui)}>Mes</span>
                <select
                  value={secondaryMonth}
                  onChange={(event) => setSecondaryMonth(event.target.value)}
                  style={{ ...periodSelectStyle(ui), minWidth: 90 }}
                >
                  {MONTH_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </label>
            </>
          ) : null}
        </div>
      </div>

      {error ? <p style={{ color: PALETTE.rose, fontSize: 13, margin: '10px 0 0' }}>{error}</p> : null}
      {loading ? <p style={{ color: ui.muted, fontSize: 13, margin: '10px 0 0' }}>Cargando datos…</p> : null}

      <div style={{ display: 'grid', gap: 8, marginTop: 10, minWidth: 0 }}>
        <SectionPanel title="Indicadores del período" ui={ui}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6, minmax(0, 1fr))', gap: 4 }}>
            {[...resultMetrics, ...funnelMetrics].map((metric) => (
              <MetricCell key={metric.label} {...metric} ui={ui} />
            ))}
          </div>
        </SectionPanel>

        {/* Dos columnas independientes: la derecha se empaqueta sin huecos por filas. */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: SUMMARY_LAYOUT_COLUMNS,
            gap: 8,
            alignItems: 'start',
            minWidth: 0,
          }}
        >
          <div style={{ display: 'grid', gap: 8, minWidth: 0, alignContent: 'start' }}>
            <SectionPanel title="Cotizado vs ventas" ui={ui}>
              <PeriodTrendEChart
                columns={selectedPeriod.columns}
                quotedBars={quotedAmountBars}
                salesBars={salesAmountBars}
                ui={ui}
              />
            </SectionPanel>

            <SectionPanel
              title="Ventas: recurrente vs 1ª compra"
              ui={ui}
              right={
                salesOriginSegmentFilter !== 'all' ? (
                  <span style={{ fontSize: 10, color: ui.muted, whiteSpace: 'nowrap' }}>
                    {salesOriginSegmentFilter}
                  </span>
                ) : null
              }
            >
              <MiniStatGrid
                ui={ui}
                columns={3}
                items={
                  salesOriginMetric === 'count'
                    ? [
                        {
                          label: 'Recurrentes',
                          value: formatCount(salesOrigin.repeatCount),
                          hint: formatPercent(salesOrigin.repeatCountShare),
                          accent: PALETTE.sales,
                        },
                        {
                          label: '1ª compra',
                          value: formatCount(salesOrigin.newCount),
                          hint: formatPercent(salesOrigin.newCountShare),
                          accent: PALETTE.sky,
                        },
                        {
                          label: 'Total',
                          value: formatCount(salesOrigin.totalCount),
                          hint: formatCurrencyCompact(salesOrigin.totalAmount),
                        },
                      ]
                    : [
                        {
                          label: 'Recurrentes $',
                          value: formatCurrencyCompact(salesOrigin.repeatAmount),
                          hint: formatPercent(salesOrigin.repeatAmountShare),
                          accent: PALETTE.sales,
                        },
                        {
                          label: '1ª compra $',
                          value: formatCurrencyCompact(salesOrigin.newAmount),
                          hint: formatPercent(salesOrigin.newAmountShare),
                          accent: PALETTE.sky,
                        },
                        {
                          label: 'Total $',
                          value: formatCurrencyCompact(salesOrigin.totalAmount),
                          hint: `${formatCount(salesOrigin.totalCount)} ventas`,
                        },
                      ]
                }
              />
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr',
                  gap: 4,
                  margin: '6px 0 4px',
                  maxWidth: 320,
                }}
              >
                <select
                  aria-label="Métrica origen ventas"
                  value={salesOriginMetric}
                  onChange={(event) => setSalesOriginMetric(event.target.value)}
                  style={{ ...periodSelectStyle(ui), minWidth: 0, width: '100%', padding: '3px 5px', fontSize: 10 }}
                >
                  {ORIGIN_METRIC_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
                <select
                  aria-label="Segmento origen ventas"
                  value={salesOriginSegmentFilter}
                  onChange={(event) => setSalesOriginSegmentFilter(event.target.value)}
                  style={{ ...periodSelectStyle(ui), minWidth: 0, width: '100%', padding: '3px 5px', fontSize: 10 }}
                >
                  <option value="all">Todos</option>
                  {orgSegmentOptions.map((segment) => (
                    <option key={segment} value={segment}>
                      {segment}
                    </option>
                  ))}
                </select>
              </div>
              <OriginSplitChart
                rows={salesOriginBars}
                metric={salesOriginMetric}
                ui={ui}
                emptyText="Sin ventas en el período"
                helpText="Recurrente = org. con compra anterior · Primera compra = sin ventas previas · arriba = % recurrentes"
                recurrentSeriesName="Clientes recurrentes"
                firstSeriesName="Primera compra"
                firstColumnLabel="1ª compra"
              />
            </SectionPanel>

            <SectionPanel
              title="Cotizaciones: recurrente vs 1ª vez"
              ui={ui}
              right={
                quoteOriginSegmentFilter !== 'all' ? (
                  <span style={{ fontSize: 10, color: ui.muted, whiteSpace: 'nowrap' }}>
                    {quoteOriginSegmentFilter}
                  </span>
                ) : null
              }
            >
              <MiniStatGrid
                ui={ui}
                columns={3}
                items={
                  quoteOriginMetric === 'count'
                    ? [
                        {
                          label: 'Ya cotizaron',
                          value: formatCount(quoteOrigin.repeatCount),
                          hint: formatPercent(quoteOrigin.repeatCountShare),
                          accent: PALETTE.sales,
                        },
                        {
                          label: '1ª cotización',
                          value: formatCount(quoteOrigin.newCount),
                          hint: formatPercent(quoteOrigin.newCountShare),
                          accent: PALETTE.sky,
                        },
                        {
                          label: 'Total',
                          value: formatCount(quoteOrigin.totalCount),
                          hint: formatCurrencyCompact(quoteOrigin.totalAmount),
                        },
                      ]
                    : [
                        {
                          label: 'Ya cotizaron $',
                          value: formatCurrencyCompact(quoteOrigin.repeatAmount),
                          hint: formatPercent(quoteOrigin.repeatAmountShare),
                          accent: PALETTE.sales,
                        },
                        {
                          label: '1ª cotización $',
                          value: formatCurrencyCompact(quoteOrigin.newAmount),
                          hint: formatPercent(quoteOrigin.newAmountShare),
                          accent: PALETTE.sky,
                        },
                        {
                          label: 'Total $',
                          value: formatCurrencyCompact(quoteOrigin.totalAmount),
                          hint: `${formatCount(quoteOrigin.totalCount)} cotiz.`,
                        },
                      ]
                }
              />
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr',
                  gap: 4,
                  margin: '6px 0 4px',
                  maxWidth: 320,
                }}
              >
                <select
                  aria-label="Métrica origen cotizaciones"
                  value={quoteOriginMetric}
                  onChange={(event) => setQuoteOriginMetric(event.target.value)}
                  style={{ ...periodSelectStyle(ui), minWidth: 0, width: '100%', padding: '3px 5px', fontSize: 10 }}
                >
                  {ORIGIN_METRIC_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
                <select
                  aria-label="Segmento origen cotizaciones"
                  value={quoteOriginSegmentFilter}
                  onChange={(event) => setQuoteOriginSegmentFilter(event.target.value)}
                  style={{ ...periodSelectStyle(ui), minWidth: 0, width: '100%', padding: '3px 5px', fontSize: 10 }}
                >
                  <option value="all">Todos</option>
                  {ticketSegmentOptions.map((segment) => (
                    <option key={segment} value={segment}>
                      {segment}
                    </option>
                  ))}
                </select>
              </div>
              <OriginSplitChart
                rows={quoteOriginBars}
                metric={quoteOriginMetric}
                ui={ui}
                emptyText="Sin cotizaciones en el período"
                helpText="Recurrente = org. que ya cotizó antes · 1ª vez = primera cotización · arriba = % recurrentes"
                recurrentSeriesName="Ya cotizaron antes"
                firstSeriesName="Primera cotización"
                firstColumnLabel="1ª cotiz."
              />
            </SectionPanel>

            <SectionPanel
              title="Distribución de tickets"
              ui={ui}
              right={
                ticketSegmentFilter !== 'all' ? (
                  <span style={{ fontSize: 10, color: ui.muted, whiteSpace: 'nowrap' }}>
                    {ticketSegmentFilter}
                  </span>
                ) : null
              }
            >
              <div style={{ marginBottom: 4, maxWidth: 160 }}>
                <select
                  aria-label="Segmento"
                  value={ticketSegmentFilter}
                  onChange={(event) => setTicketSegmentFilter(event.target.value)}
                  style={{ ...periodSelectStyle(ui), minWidth: 0, width: '100%', padding: '3px 5px', fontSize: 10 }}
                >
                  <option value="all">Todos</option>
                  {ticketSegmentOptions.map((segment) => (
                    <option key={segment} value={segment}>
                      {segment}
                    </option>
                  ))}
                </select>
              </div>
              <TicketDistributionChart rows={ticketDistribution} ui={ui} />
            </SectionPanel>

            <SectionPanel
              title="Top 10 organizaciones"
              ui={ui}
              right={
                <span style={{ fontSize: 10, color: ui.muted, whiteSpace: 'nowrap' }}>
                  Top3 {formatPercent(concentration.top3Share)}
                  {orgSegmentFilter !== 'all' ? ` · ${orgSegmentFilter}` : ''}
                </span>
              }
            >
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr',
                  gap: 4,
                  marginBottom: 4,
                }}
              >
                <select
                  aria-label="Métrica"
                  value={orgTopMetric}
                  onChange={(event) => setOrgTopMetric(event.target.value)}
                  style={{ ...periodSelectStyle(ui), minWidth: 0, width: '100%', padding: '3px 5px', fontSize: 10 }}
                >
                  {ORG_TOP_METRIC_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
                <select
                  aria-label="Segmento"
                  value={orgSegmentFilter}
                  onChange={(event) => setOrgSegmentFilter(event.target.value)}
                  style={{ ...periodSelectStyle(ui), minWidth: 0, width: '100%', padding: '3px 5px', fontSize: 10 }}
                >
                  <option value="all">Todos</option>
                  {orgSegmentOptions.map((segment) => (
                    <option key={segment} value={segment}>
                      {segment}
                    </option>
                  ))}
                </select>
              </div>
              <RankingList
                rows={topOrganizations}
                color={PALETTE.sales}
                ui={ui}
                showShareOf={orgTopShareBase}
                valueMode={orgTopMetric === 'count' ? 'count' : 'currency'}
                secondaryMode={orgTopMetric === 'count' ? 'amount' : 'count'}
              />
            </SectionPanel>

            <SectionPanel
              title="Desempeño por ejecutivo"
              ui={ui}
              right={
                <span style={{ fontSize: 10, color: ui.muted }}>
                  {formatCount(metrics.uniqueExecs)} ej.
                </span>
              }
            >
              <DataTable
                ui={ui}
                empty="Sin cotizaciones para rankear ejecutivos"
                columns={[
                  { key: 'label', label: 'Ejecutivo', maxWidth: 100 },
                  { key: 'quotes', label: 'Cot.', align: 'right', render: (r) => formatCount(r.quotes) },
                  {
                    key: 'emitted',
                    label: 'Emit.',
                    align: 'right',
                    render: (r) => formatCount(r.emitted),
                    color: () => PALETTE.sky,
                  },
                  {
                    key: 'draft',
                    label: 'Pend.',
                    align: 'right',
                    render: (r) => formatCount(r.draft),
                    color: () => PALETTE.amber,
                  },
                  { key: 'won', label: 'Gan.', align: 'right', render: (r) => formatCount(r.won), color: () => PALETTE.green },
                  { key: 'lost', label: 'Perd.', align: 'right', render: (r) => formatCount(r.lost), color: () => PALETTE.coral },
                  {
                    key: 'winRate',
                    label: 'Win%',
                    align: 'right',
                    bold: true,
                    render: (r) => formatPercent(r.winRate),
                  },
                  {
                    key: 'wonAmount',
                    label: 'Ganado',
                    align: 'right',
                    render: (r) => formatCurrencyCompact(r.wonAmount),
                    color: () => PALETTE.sales,
                  },
                ]}
                rows={executivePerformance}
              />
            </SectionPanel>

            <SectionPanel title="Licitaciones" ui={ui}>
              <MiniStatGrid
                ui={ui}
                items={[
                  {
                    label: 'Abiertas',
                    value: `${formatCount(metrics.openLicitations)}`,
                    hint: formatCurrencyCompact(metrics.openLicitationAmount),
                    accent: PALETTE.sky,
                  },
                  {
                    label: 'Ganadas',
                    value: formatCount(metrics.wonLicitations),
                    hint: formatCurrencyCompact(metrics.wonLicitationAmount),
                    accent: PALETTE.green,
                  },
                  {
                    label: 'Win rate',
                    value: formatPercent(metrics.licitationWinRate),
                    hint: `${metrics.wonLicitations} / ${metrics.wonLicitations + metrics.lostLicitations} cerradas`,
                  },
                  {
                    label: 'Pipeline lic.',
                    value: formatCurrencyCompact(metrics.licitationAmount),
                    hint: `${formatCount(metrics.totalLicitations)} en período`,
                  },
                ]}
              />
              <div style={{ marginTop: 8 }}>
                <BreakdownRows rows={licitationStatusMix} ui={ui} valueMode="count" compact />
              </div>
            </SectionPanel>
          </div>

          <div style={{ display: 'grid', gap: 8, minWidth: 0, alignContent: 'start' }}>
            <SectionPanel title="Embudo por estado" ui={ui}>
              <BreakdownRows rows={funnelRows} ui={ui} valueMode="count" compact />
              <div style={{ marginTop: 8 }}>
                <MiniStatGrid
                  ui={ui}
                  items={[
                    { label: 'Borrador $', value: formatCurrencyCompact(metrics.draftAmount), hint: `${metrics.draftQuotes} cotiz.` },
                    { label: 'Emitida $', value: formatCurrencyCompact(metrics.emitidaAmount), hint: `${metrics.emitidaQuotes} cotiz.`, accent: PALETTE.sky },
                    { label: 'Cobertura', value: formatPercent(metrics.coverage), hint: 'Ganado / pipeline' },
                    { label: 'Cancelado', value: formatCurrencyCompact(metrics.cancelledAmount), hint: `${metrics.cancelledQuotes} cotiz.`, accent: PALETTE.rose },
                  ]}
                />
              </div>
            </SectionPanel>

            <SectionPanel title="Mix por canal" ui={ui}>
              <BreakdownRows rows={channelMix} ui={ui} compact />
            </SectionPanel>

            <SectionPanel title="Concentración de clientes" ui={ui}>
              <MiniStatGrid
                ui={ui}
                columns={1}
                items={[
                  {
                    label: 'Cliente #1',
                    value: formatPercent(concentration.top1Share),
                    hint: topOrganizations[0]?.label || '—',
                    accent: PALETTE.sky,
                  },
                  {
                    label: 'Top 3 clientes',
                    value: formatPercent(concentration.top3Share),
                    hint: `sobre ${formatCurrencyCompact(concentration.baseAmount)} ventas${
                      orgSegmentFilter !== 'all' ? ` · ${orgSegmentFilter}` : ''
                    }`,
                    accent: PALETTE.sales,
                  },
                  {
                    label: 'Orgs con ventas',
                    value: formatCount(concentration.orgsWon),
                    hint:
                      orgSegmentFilter === 'all'
                        ? `de ${formatCount(metrics.uniqueOrgs)} organizaciones tocadas`
                        : `en segmento ${orgSegmentFilter}`,
                  },
                ]}
              />
            </SectionPanel>

            <SectionPanel title="Conversión y tasas" ui={ui}>
              <MiniStatGrid
                ui={ui}
                columns={1}
                items={[
                  {
                    label: 'Cotiz. → venta',
                    value: formatPercent(metrics.quoteToSale),
                    hint: 'Vendido / cotizado (c/IVA)',
                    accent: PALETTE.quoted,
                  },
                  {
                    label: 'Tasa cierre',
                    value: formatPercent(metrics.winRate),
                    hint: `${metrics.soldQuotes} vend. / ${metrics.soldQuotes + metrics.lostQuotes} resueltas`,
                    accent: PALETTE.sales,
                  },
                  {
                    label: 'Hit rate',
                    value: formatPercent(metrics.hitRate),
                    hint: `${metrics.wonQuotes} ganadas / ${metrics.decidedQuotes} decididas`,
                  },
                  {
                    label: 'Conv. embudo',
                    value: formatPercent(metrics.conversion),
                    hint: `${metrics.wonQuotes} / ${metrics.emittedQuotes} emitidas`,
                  },
                  {
                    label: 'Ratio pérdida',
                    value: formatPercent(metrics.lossRatio),
                    hint: 'Perdido / (ganado + perdido)',
                    accent: PALETTE.coral,
                  },
                ]}
              />
            </SectionPanel>

            <SectionPanel title="Actividad del período" ui={ui}>
              <MiniStatGrid
                ui={ui}
                columns={1}
                items={[
                  {
                    label: 'Organizaciones',
                    value: formatCount(metrics.uniqueOrgs),
                    hint: `${formatCount(metrics.totalQuotes)} cotizaciones`,
                  },
                  {
                    label: 'Ejecutivos activos',
                    value: formatCount(metrics.uniqueExecs),
                    hint: `${formatCount(metrics.openQuotes)} abiertas`,
                    accent: PALETTE.sky,
                  },
                  {
                    label: 'Ticket cotizado',
                    value: formatCurrencyCompact(metrics.avgQuoteTicket),
                    hint: 'Promedio c/IVA',
                  },
                  {
                    label: 'Ticket venta',
                    value: formatCurrencyCompact(metrics.avgSaleTicket),
                    hint: `${formatCount(metrics.salesCount)} vendidas`,
                    accent: PALETTE.sales,
                  },
                  {
                    label: 'Pipeline',
                    value: formatCurrencyCompact(metrics.pipelineAmount),
                    hint: `${formatPercent(metrics.pipelineShare)} del cotizado`,
                    accent: PALETTE.amber,
                  },
                ]}
              />
            </SectionPanel>

            <SectionPanel title="Top ejecutivos" ui={ui}>
              <RankingBarChart rows={topExecutives} color={PALETTE.quoted} ui={ui} />
            </SectionPanel>

            <SectionPanel title="Logística / despachos" ui={ui}>
              <MiniStatGrid
                ui={ui}
                items={[
                  {
                    label: 'Entrega OK',
                    value: formatPercent(metrics.logisticsDeliveryRate),
                    hint: `${metrics.deliveredLogistics}/${metrics.totalLogistics}`,
                    accent: PALETTE.sales,
                  },
                  {
                    label: 'Pendientes',
                    value: formatCount(metrics.pendingLogistics),
                    hint: 'Programado / en tránsito',
                    accent: PALETTE.amber,
                  },
                  {
                    label: 'Incidencias',
                    value: formatCount(metrics.incidentLogistics),
                    hint: formatPercent(metrics.logisticsIncidentRate),
                    accent: PALETTE.rose,
                  },
                  {
                    label: 'Total despachos',
                    value: formatCount(metrics.totalLogistics),
                    hint: 'En el período filtrado',
                  },
                ]}
              />
              <div style={{ marginTop: 8 }}>
                <BreakdownRows rows={logisticsStatusMix} ui={ui} valueMode="count" compact />
              </div>
            </SectionPanel>
          </div>
        </div>
      </div>
    </div>

  );
};

export default PaintCommercialSummaryWorkspace;
